// src/ThemeContext.tsx

import React, { createContext, useMemo, useState, useContext } from 'react';
import { ThemeProvider, createTheme, CssBaseline, PaletteMode } from '@mui/material';

interface ThemeContextType {
  toggleTheme: () => void;
  mode: PaletteMode;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<PaletteMode>('light');

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  // Use the mode value here to dynamically create the theme
  const theme = useMemo(() =>
    createTheme({
      palette: {
        mode,
        primary: {
          main: '#042E4B',
        },
        secondary: {
          main: '#EF453D',
        },
        error: {
          main: '#d32f2f',
        },
        background: {
          default: mode === 'light' ? '#f5f5f5' : '#333333',
        },
        text: {
          primary: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : '#ffffff',
          secondary: mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
        },
      },
      components: {
        MuiListItemText: {
          styleOverrides: {
            secondary: {
              color: mode === 'light' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)',
              fontWeight: 500,
            },
          },
        },
      },
    }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={{ toggleTheme, mode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};